// Per-biome decorative SET PIECES — a seeded multi-prop formation (boulder field, cactus arc,
// grave rows, crystal cluster) the game stamps onto a POI so each world has recognizable, hand-
// made-looking landmarks built from existing sprites. Pure + deterministic over the passed rng;
// returns local offsets + sprite + scale, the caller maps them to world positions (off-cliff).
// The caller routes each piece through propFootprint (CF5): big centrepieces become solid obstacles,
// small ones stay pass-through props — so a landmark set-piece reads as something you route around.

export interface SetPiece {
  dx: number
  dz: number
  sprite: string
  scale: number
}

interface Rng {
  next(): number
  int(min: number, max: number): number
}

const TAU = Math.PI * 2

const FRAME_SPRITES: Record<string, string[]> = {
  meadow: ['prop:moonrock', 'prop:rock'],
  dunes: ['prop:redrock', 'prop:cactus'],
  graveyard: ['prop:grave', 'prop:pillar'],
  rift: ['prop:crystal', 'prop:pillar'],
}

/**
 * A small decorative ring of markers framing an interactable (Moai / microwave / shrine) so it
 * reads as a deliberate place, not a prop dropped on bare ground. One segment is left open so it
 * never visually pens the player in. Pure + deterministic; sprites are biome-specific (small scale).
 */
export function frameRing(rng: Rng, biomeId: string): SetPiece[] {
  const sprites = FRAME_SPRITES[biomeId] ?? FRAME_SPRITES.meadow
  const n = 5 + rng.int(0, 1) // 5–6 markers
  const rad = 3.2 + rng.next() * 0.8
  const gap = rng.int(0, n - 1) // one opening — never a sealed pen
  const out: SetPiece[] = []
  for (let s = 0; s < n; s++) {
    if (s === gap) continue
    const a = (s / n) * TAU + (rng.next() - 0.5) * 0.15
    const sprite = sprites[rng.int(0, sprites.length - 1)]
    out.push({ dx: Math.cos(a) * rad, dz: Math.sin(a) * rad, sprite, scale: 0.9 + rng.next() * 0.6 })
  }
  return out
}

/** A seeded decorative formation for a biome (defaults to the meadow boulder field). */
export function biomeSetPiece(rng: Rng, biomeId: string): SetPiece[] {
  const out: SetPiece[] = []
  switch (biomeId) {
    case 'dunes': {
      // a sweeping cactus + redrock arc, like a wind-carved dune ridge
      const n = 5 + rng.int(0, 2)
      const spread = 0.7 + rng.next() * 0.6
      const base = rng.next() * TAU
      for (let i = 0; i < n; i++) {
        const a = base + (i / (n - 1) - 0.5) * spread
        const r = 5 + rng.next() * 2
        const sprite = rng.next() < 0.5 ? 'prop:cactus' : 'prop:redrock'
        out.push({ dx: Math.cos(a) * r, dz: Math.sin(a) * r, sprite, scale: 1.6 + rng.next() * 1.0 })
      }
      break
    }
    case 'graveyard': {
      // tidy grave rows with a dead tree watching over them
      const rows = 2 + rng.int(0, 1)
      const cols = 3
      const gx = 2.4
      const gz = 2.0
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          out.push({ dx: (c - (cols - 1) / 2) * gx, dz: (r - (rows - 1) / 2) * gz, sprite: 'prop:grave', scale: 1.4 + rng.next() * 0.5 })
        }
      }
      out.push({ dx: (rows + 1) * gz * 0.6, dz: 0, sprite: 'prop:deadtree', scale: 2.4 + rng.next() * 0.8 })
      break
    }
    case 'rift': {
      // a jagged crystal cluster of mixed sizes around a tall central shard
      const n = 5 + rng.int(0, 3)
      out.push({ dx: 0, dz: 0, sprite: 'prop:crystal', scale: 3.0 + rng.next() * 1.2 })
      for (let i = 0; i < n; i++) {
        const a = rng.next() * TAU
        const r = 1.6 + rng.next() * 3.2
        out.push({ dx: Math.cos(a) * r, dz: Math.sin(a) * r, sprite: 'prop:crystal', scale: 1.2 + rng.next() * 1.6 })
      }
      break
    }
    default: {
      // meadow (and fallback): a loose boulder field with a flag planted in it
      const n = 4 + rng.int(0, 2)
      for (let i = 0; i < n; i++) {
        const a = rng.next() * TAU
        const r = 2 + rng.next() * 4.5
        const sprite = rng.next() < 0.5 ? 'prop:moonrock' : 'prop:rock'
        out.push({ dx: Math.cos(a) * r, dz: Math.sin(a) * r, sprite, scale: 1.5 + rng.next() * 1.1 })
      }
      out.push({ dx: 0, dz: 0, sprite: 'prop:flag', scale: 2.4 + rng.next() * 0.6 })
      break
    }
  }
  return out
}
